import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight,
  BarChart3,
  Target,
  Users,
  Network,
  CheckCircle,
  Calendar
} from 'lucide-react';

// WhatsApp Icon SVG Component
const WhatsAppIcon = ({ className = "" }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Navigation Component - Neo-Brutalist Clean
const Navigation = () => (
  <nav className="sticky top-0 z-50 bg-cream border-b-4 border-text-dark" data-testid="sistemas-navigation">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center py-4">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <span className="font-poppins font-black text-2xl md:text-3xl tracking-tight text-text-dark">
            BOOST<span className="text-pink">CHANGE</span>
          </span>
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/" className="hidden md:flex items-center gap-2 font-poppins font-semibold text-text-dark hover:text-pink transition-colors">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
          <a
            href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20me%20interesa%20conocer%20m%C3%A1s%20sobre%20sus%20servicios%20de%20consultor%C3%ADa%20en%20Sistemas%20de%20Impacto.%20Me%20gustar%C3%ADa%20conversar%20sobre%20mi%20organizaci%C3%B3n."
            target="_blank"
            rel="noopener noreferrer"
            className="bg-purple text-white font-poppins font-bold uppercase tracking-widest px-6 py-3 border-4 border-text-dark hover:translate-y-1 transition-all"
            style={{ boxShadow: '5px 5px 0px #000', borderRadius: '0px' }}
            data-testid="sistemas-cta"
          >
            Consulta gratis
          </a>
        </div>
      </div>
    </div>
  </nav>
);

// Hero Section - Cream background, cyan accent
const HeroSection = () => (
  <section className="bg-cream py-16 md:py-24" data-testid="sistemas-hero">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="space-y-8">
          <div 
            className="inline-block bg-white px-5 py-2"
            style={{ 
              border: '3px solid #1a1a1a',
              boxShadow: '4px 4px 0px #000',
              borderRadius: '0px'
            }}
          >
            <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">Sistemas de Impacto</span>
          </div>
          
          <h1 className="font-poppins font-black text-4xl md:text-5xl lg:text-6xl text-text-dark leading-[1.05]" data-testid="sistemas-title">
            Consultoría que{' '}
            <span className="bg-cyan text-text-dark px-2">no termina</span>{' '}
            en un cajón
          </h1>
          
          <p className="font-poppins text-lg md:text-xl text-text-dark/80 leading-relaxed">
            Mide lo que importa, de forma viva y sin burocracia. 
            Sistemas que tu equipo realmente usa.
          </p>

          <div className="flex flex-wrap gap-4 pt-4">
            <a
              href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20me%20interesa%20conocer%20m%C3%A1s%20sobre%20sus%20servicios%20de%20consultor%C3%ADa%20en%20Sistemas%20de%20Impacto.%20Me%20gustar%C3%ADa%20conversar%20sobre%20mi%20organizaci%C3%B3n."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-cyan text-text-dark font-poppins font-bold uppercase tracking-widest px-8 py-4 border-4 border-text-dark hover:translate-y-1 transition-all"
              style={{ 
                boxShadow: '6px 6px 0px #000',
                borderRadius: '0px'
              }}
              data-testid="sistemas-hero-cta"
            >
              <WhatsAppIcon className="w-5 h-5" />
              AGENDA UNA CONSULTA
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Visual - Clean rectangular frame, adjusted for square image */}
        <div className="relative">
          <div 
            className="w-full overflow-hidden bg-white"
            style={{ 
              border: '4px solid #1a1a1a',
              boxShadow: '8px 8px 0px #000',
              borderRadius: '0px',
              aspectRatio: '1 / 1'
            }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_project-status-62/artifacts/mb3ekbz2_20250526_115302.jpg"
              alt="Sesión de trabajo colaborativo"
              className="w-full h-full"
              style={{ display: 'block', borderRadius: '0px', objectFit: 'cover', objectPosition: 'center' }}
            />
          </div>
          
          {/* Floating element - No rotation */}
          <div 
            className="absolute -bottom-4 -right-4 bg-yellow px-4 py-2"
            style={{ 
              border: '3px solid #1a1a1a',
              boxShadow: '4px 4px 0px #000',
              borderRadius: '0px'
            }}
          >
            <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">Dashboards vivos!</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section - 4 Brutalist Cards with updated content
const ServicesSection = () => {
  const services = [
    {
      icon: <BarChart3 className="w-8 h-8" strokeWidth={2.5} />,
      title: "Frameworks MEL y Aprendizaje",
      iconBg: "bg-cyan",
      description: "Más que medir por cumplir, construimos sistemas para que tu equipo aprenda y tome decisiones. Transformamos los datos en flujos vivos que te permiten robustecer tu estrategia, adaptarte ágilmente e innovar constantemente.",
      bullets: [
        "Sistemas que dan sentido a los datos",
        "Flujos de evaluación continua",
        "Dashboards interactivos"
      ]
    },
    {
      icon: <Users className="w-8 h-8" strokeWidth={2.5} />,
      title: "Evaluación Participativa",
      iconBg: "bg-pink",
      description: "Ponemos a las personas en el centro. Diseñamos evaluaciones horizontales (cualitativas y cuantitativas) en las que las personas son el centro. Agregamos valor a tu operación y empoderamos a las comunidades con las que trabajas a través de evaluaciones participativas que agregan valor.",
      bullets: [
        "Metodologías SenseMaker",
        "Outcome Harvesting (Cosecha de Alcances)",
        "Historias de cambio",
        "Evaluación centrada en el usuario"
      ]
    },
    {
      icon: <Target className="w-8 h-8" strokeWidth={2.5} />,
      title: "Estrategias y Planeación",
      iconBg: "bg-yellow",
      description: "El día a día operativo te consume; nosotros te ayudamos a dar un paso atrás para definir el norte. Facilitamos espacios de reflexión y creamos recursos estratégicos útiles que se implementan, no que se archivan.",
      bullets: [
        "Teoría de Cambio viva",
        "Planes Operativos Anuales (POA)",
        "Estrategias multianuales",
        "Talleres ágiles de planeación"
      ]
    },
    {
      icon: <Network className="w-8 h-8" strokeWidth={2.5} />,
      title: "Modelos de Intervención",
      iconBg: "bg-lime",
      description: "Co-diseñamos proyectos y estrategias de impacto que agregan valor a todos los involucrados. Impulsamos ecosistemas de colaboración y estructuramos mecanismos listos para escalar y aplicar a financiamiento.",
      bullets: [
        "Mapeo de ecosistemas",
        "Diseño de consorcios",
        "Proyectos \"proposal-ready\" (Propuestas ganadoras)"
      ]
    },
  ];

  return (
    <section className="bg-cream py-16 md:py-24" data-testid="sistemas-services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-poppins font-black text-3xl md:text-5xl text-text-dark mb-4">
            NUESTROS SERVICIOS
          </h2>
          <p className="font-poppins font-medium text-lg text-text-dark/70">Consultoría especializada para el sector social</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service) => (
            <div
              key={service.title}
              className="bg-white p-8"
              style={{ 
                border: '4px solid #1a1a1a',
                boxShadow: '8px 8px 0px #000',
                borderRadius: '0px'
              }}
              data-testid={`service-block-${service.title}`}
            >
              {/* Icon container */}
              <div 
                className={`${service.iconBg} p-4 inline-block mb-5`}
                style={{ 
                  border: '3px solid #1a1a1a',
                  borderRadius: '0px'
                }}
              >
                <div className="text-text-dark">{service.icon}</div>
              </div>
              
              <h3 className="font-poppins font-bold text-xl md:text-2xl text-text-dark mb-4">{service.title}</h3>
              <p className="font-poppins text-text-dark/70 mb-6 leading-relaxed">{service.description}</p>
              
              <ul className="space-y-2">
                {service.bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-purple flex-shrink-0 mt-0.5" />
                    <span className="font-poppins text-sm text-text-dark">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Case Studies Section - Cream background, white brutalist cards
const CaseStudiesSection = () => {
  const cases = [
    {
      badge: "CASO DE ÉXITO",
      title: "Asoc. de Construcción en Seco del Ecuador (ACSEC)",
      subtitle: "Planeación Estratégica 2026 - 2030",
      description: "Acompañamos a este nuevo gremio (nacido en 2026) en la construcción ágil y participativa de su norte estratégico junto a su Directorio. Definimos su visión, misión, teoría de cambio e indicadores de gestión para una estrategia del 2026 al 2030.",
      result: "Estrategia ejecutiva + Herramienta digital de seguimiento",
      badgeColor: "bg-cyan"
    },
    {
      badge: "CASO DE ÉXITO",
      title: "Unidos por la Educación & Corp. Líderes Para Gobernar",
      subtitle: "Proyecto Fibras: Modelo de Intervención",
      description: "Diseñamos un modelo de gestión integral para impulsar el empoderamiento comunitario en escuelas rurales de Ecuador. Estructuramos el ecosistema completo: desde el modelo operativo, su presupuesto y herramientas para el levantamiento de fondos con cooperantes, incluida una landing page.",
      result: "Modelo de gestión + Landing Page",
      badgeColor: "bg-pink"
    },
  ];

  return (
    <section className="bg-cream py-16 md:py-24" data-testid="sistemas-cases">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-poppins font-black text-3xl md:text-5xl text-text-dark mb-4">
            HISTORIAS REALES
          </h2>
          <p className="font-poppins font-medium text-lg text-text-dark/70">Casos de éxito de nuestros clientes</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {cases.map((caseStudy) => (
            <div
              key={caseStudy.title}
              className="bg-white p-8"
              style={{ 
                border: '4px solid #1a1a1a',
                boxShadow: '8px 8px 0px #000',
                borderRadius: '0px'
              }}
              data-testid={`case-study-${caseStudy.title}`}
            >
              {/* Badge */}
              <div 
                className={`${caseStudy.badgeColor} px-4 py-2 inline-block mb-5`}
                style={{ 
                  border: '3px solid #1a1a1a',
                  borderRadius: '0px'
                }}
              >
                <span className="font-poppins font-bold text-xs uppercase text-text-dark tracking-wide">{caseStudy.badge}</span>
              </div>
              
              <h3 className="font-poppins font-black text-xl md:text-2xl text-text-dark mb-2">{caseStudy.title}</h3>
              <p className="font-poppins font-medium text-base text-purple mb-4">{caseStudy.subtitle}</p>
              
              <p className="font-poppins text-text-dark/80 mb-6 leading-relaxed">{caseStudy.description}</p>
              
              {/* Result badge - Lima background */}
              <div 
                className="bg-lime px-4 py-2 inline-block"
                style={{ 
                  border: '3px solid #1a1a1a',
                  borderRadius: '0px'
                }}
              >
                <span className="font-poppins font-bold text-sm text-text-dark">{caseStudy.result}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Differentiator Section - Keep structure, update text
const DifferentiatorSection = () => (
  <section className="bg-cream py-16 md:py-24" data-testid="sistemas-diff">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Image with cyan frame */}
        <div className="relative">
          <div 
            className="bg-cyan p-3"
            style={{ 
              border: '4px solid #1a1a1a',
              boxShadow: '8px 8px 0px #000',
              borderRadius: '0px'
            }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_project-status-62/artifacts/s6svpzb7_IMG-20250410-WA0005.jpg"
              alt="Equipo colaborando"
              className="w-full h-auto"
              style={{ 
                border: '3px solid #1a1a1a',
                display: 'block',
                borderRadius: '0px'
              }}
            />
          </div>
        </div>
        
        <div className="space-y-6">
          {/* Yellow badge */}
          <div 
            className="bg-yellow px-5 py-2 inline-block"
            style={{ 
              border: '3px solid #1a1a1a',
              boxShadow: '4px 4px 0px #000',
              borderRadius: '0px'
            }}
          >
            <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">Nuestro diferenciador</span>
          </div>
          
          <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark leading-tight">
            Sistemas hechos para <span className="text-purple">operar</span>, no para archivar
          </h2>
          
          <p className="font-poppins text-lg text-text-dark/80 leading-relaxed">
            Diseñamos soluciones que se integran naturalmente a tu operación diaria. Transformamos evaluaciones y estudios complejos en herramientas prácticas que tu equipo realmente puede usar. Construimos flujos ágiles que te permiten basar tus decisiones en evidencia, todos los días.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
            {[
              "Precisión de principio a fin",
              "Colaboración horizontal",
              "Voces reales del territorio",
              "Resultados medibles en tiempo real"
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-purple flex-shrink-0" />
                <span className="font-poppins text-sm text-text-dark">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// CTA Section - Purple background, cream button
const CTASection = () => (
  <section className="bg-purple py-16" data-testid="sistemas-cta-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="font-poppins font-black text-3xl md:text-4xl text-white mb-6">
        ¿Listo para medir lo que realmente importa?
      </h2>
      <p className="font-poppins text-lg md:text-xl text-white/80 mb-8">
        Agenda una llamada de exploración gratuita y construyamos el sistema que tu organización necesita.
      </p>
      <a
        href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20estoy%20listo%20para%20medir%20lo%20que%20importa.%20Quiero%20agendar%20una%20llamada%20de%20exploraci%C3%B3n%20para%20consultor%C3%ADa."
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-3 bg-cream text-text-dark font-poppins font-bold uppercase tracking-widest px-8 py-4 hover:translate-y-1 transition-all"
        style={{ 
          border: '4px solid #1a1a1a',
          boxShadow: '6px 6px 0px #000',
          borderRadius: '0px'
        }}
        data-testid="sistemas-final-cta"
      >
        <Calendar className="w-5 h-5" />
        AGENDAR LLAMADA
        <ArrowRight className="w-5 h-5" />
      </a>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-text-dark py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <Link to="/" className="font-poppins font-black text-2xl text-white">
          BOOST<span className="text-pink">CHANGE</span>
        </Link>
        <div className="flex gap-6">
          <Link to="/" className="font-poppins text-white/80 hover:text-pink transition-colors">Inicio</Link>
          <Link to="/impact-boost-academy" className="font-poppins text-white/80 hover:text-pink transition-colors">Academy</Link>
          <Link to="/boost-ops" className="font-poppins text-white/80 hover:text-pink transition-colors">Boost Ops</Link>
        </div>
        <p className="font-poppins text-white/60 text-sm">2024 Boost Change</p>
      </div>
    </div>
  </footer>
);

// Main Page Component
const SistemasDeImpactoPage = () => {
  return (
    <div className="min-h-screen bg-cream" data-testid="sistemas-page">
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <CaseStudiesSection />
        <DifferentiatorSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default SistemasDeImpactoPage;
